import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { InviteCategory } from '@/domain/external-invitation/entities/invitation-category';
import { Sector } from '@/domain/impact/impact.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSDateField } from '@/ui/components/input/FSDateField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { translate } from '@/ui/i18n';
import { isEmail } from '@/ui/utils/form-validations';

export type InviteSubmitData = {
  name: string;
  email: string;
  limitDate: string;
  category: InviteCategory;
  sector: Sector;
};

type InviteForm = {
  name: string;
  email: string;
  limitDate: string;
  category: InviteCategory;
};

export function FormExternalInvite({
  sector,
  categories = [],
  onSubmitInvite,
}: {
  sector: Sector;
  categories?: InviteCategory[];
  onSubmitInvite: (data: InviteSubmitData) => void;
}) {
  const defaultValues: InviteForm = {
    name: '',
    email: '',
    limitDate: '',
    category: categories[0] || '',
  };
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<InviteForm>({ defaultValues });

  useEffect(() => {
    reset(defaultValues); // useful to update category list when change tab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sector, reset]);

  const handleFormSubmit = async (form: InviteForm) => {
    const submitData: InviteSubmitData = {
      name: form.name,
      email: form.email,
      limitDate: form.limitDate,
      category: form.category,
      sector: sector,
    };
    onSubmitInvite(submitData);
    // HACK use timeout because date-field did not reset on submit
    setTimeout(
      () =>
        reset({
          ...defaultValues,
          category: form.category, // keep same category
        }),
      1,
    );
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: '900' }}>
          {sector ? translate(`InviteExternalToFill.title.${sector}`) : ''}
        </Typography>
        <Typography>
          {sector ? translate(`InviteExternalToFill.sub-title.${sector}`) : ''}
        </Typography>
      </Stack>
      <Box sx={{ my: 2 }}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <FSTextField
              label={translate(
                'InviteExternalToFill.create-and-send-invitation.full-name',
              )}
              register={{
                ...register('name', {
                  required: translate(
                    'InviteExternalToFill.create-and-send-invitation.name-is-required',
                  ),
                }),
              }}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <FSTextField
              label={translate(
                'InviteExternalToFill.create-and-send-invitation.email',
              )}
              register={{
                ...register('email', {
                  required: translate(
                    'InviteExternalToFill.create-and-send-invitation.email-is-required',
                  ),
                  validate: isEmail,
                }),
              }}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <Box sx={{ minWidth: '9rem' }}>
              <Controller
                name={'limitDate'}
                control={control}
                render={({ field }) => (
                  <FSDateField
                    label={translate(
                      'InviteExternalToFill.create-and-send-invitation.limit-date',
                    )}
                    value={field.value}
                    onChange={field.onChange}
                    name={field.name}
                    error={!!errors.limitDate}
                    helperText={errors.limitDate?.message}
                  />
                )}
              />
            </Box>
            {categories.length > 0 && (
              <Box sx={{ minWidth: '9rem' }}>
                <Controller
                  name={'category'}
                  control={control}
                  render={({ field }) => (
                    <FSTextField
                      select
                      label={translate(
                        'InviteExternalToFill.create-and-send-invitation.category',
                      )}
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      disabled={field.disabled}
                    >
                      {categories.map((category) => (
                        <MenuItem key={`${sector}${category}`} value={category}>
                          {translate(
                            `InviteExternalToFill.category.${category}`,
                          )}
                        </MenuItem>
                      ))}
                    </FSTextField>
                  )}
                />
              </Box>
            )}
            <Box sx={{ my: 1 }}>
              <FSPrimaryButton type="submit">
                {translate('InviteExternalToFill.submit-button-text')}
              </FSPrimaryButton>
            </Box>
          </Stack>
        </form>
      </Box>
    </Box>
  );
}
